import { useEffect, useRef } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';

import { trackEvent } from '../utils/trackEvent';

/**
 * Fires an impression event once the returned ref's element becomes visible. Mirrors the
 * matches-coupon mechanism: impression tracking only activates when `eventName` is provided.
 *
 * The `firedRef` guard ensures the event fires at most once per mount: without it the impression
 * would re-fire every time the element scrolls back into view (isIntersecting false → true), or
 * whenever `eventData`/`dataLayer` identity changes while the element stays visible.
 */
export function useTrackImpression(
  eventName?: string,
  eventData?: Record<string, unknown>,
  dataLayer?: unknown[] | undefined,
) {
  const { isIntersecting, ref } = useIntersectionObserver({ threshold: 0.5 });
  const firedRef = useRef(false);

  useEffect(() => {
    if (eventName && isIntersecting && !firedRef.current) {
      trackEvent(eventName, eventData, dataLayer);
      firedRef.current = true;
    }
  }, [dataLayer, eventData, eventName, isIntersecting]);

  return ref;
}
